import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router'
import useInterval from 'use-interval'
import { useAppDispatch } from 'store/hooks'
import { actions as companyActions } from 'store/companySearch'
import { RootState } from 'store/'
import { Box, Button, CircularProgress, Divider, Link, Tooltip, Typography } from '@mui/material'

import { CompanyBenchmarkResponse, CompanySearchForBenchmarkStatus, Stats } from 'store/companySearch/types'
import { useIsBoAdminRole } from 'hooks/useIsBoAdminRole'
import { CompanySearchLogs } from 'containers/search/components/CompanySearchLogs'
import BenchmarkReportGenerator from 'containers/benchmark/benchmarkReportGenerator/BenchmarkReportGenerator'
import { useBenchmarkExists } from 'containers/dashboard/benchmarkReport/hooks/useBenchmarkExists'

export default function CompanyBenchmarkWaitPage() {
  const dispatch = useAppDispatch()

  const isAdminRole = useIsBoAdminRole()
  const [selectedIpcrCodes, setSelectedIpcrCodes] = useState<string[]>([])
  const companyBecnhmarkResponse: CompanyBenchmarkResponse | null = useSelector(
    (state: RootState) => state.companySearch.benchmarkResponse,
  )
  const companySearchStatusRequestError = useSelector((state: RootState) => state.companySearch.error.searchStatus)
  const searchForBenchmarkStatus: CompanySearchForBenchmarkStatus | undefined = companyBecnhmarkResponse?.status
  const stats: Stats | undefined = companyBecnhmarkResponse?.stats
  const companyNamesSelectedList = companyBecnhmarkResponse?.company_names.split('||||')
  const companyNamesWithAliasesArray = stats?.companyNamesWithAliases?.split('||||')

  const isStatusError = searchForBenchmarkStatus === 'error'
  const isFinished = searchForBenchmarkStatus === 'winning_patent_finished'
  const isProcessing = companyBecnhmarkResponse && !isFinished && !isStatusError
  const { id } = useParams<{ id?: string }>()
  const { exists } = useBenchmarkExists(companyBecnhmarkResponse?.completion_action_id)

  useEffect(() => {
    if (id) dispatch(companyActions.getCompanySearchForBenchmarkStatus(id))
  }, [id, dispatch])

  useEffect(() => {
    if (stats?.winningIpcrCodes) {
      setSelectedIpcrCodes(stats.winningIpcrCodes)
    }
  }, [stats?.winningIpcrCodes])

  useInterval(
    () => {
      if (id) dispatch(companyActions.getCompanySearchForBenchmarkStatus(id))
    },
    isFinished && isStatusError ? 5000 : null,
  )

  return (
    <Box m={5}>
      <Typography variant="h4">Benchmark Report generation</Typography>
      {companySearchStatusRequestError && (
        <Typography color="error" mt={1}>
          {companySearchStatusRequestError}
        </Typography>
      )}
      {companyBecnhmarkResponse && (
        <>
          <Typography variant="h6" sx={{ mt: 3 }}>
            Company Name(s):
            <Tooltip title={companyNamesWithAliasesArray?.join(', ')}>
              <Typography variant="h6" component="span" color="primary" sx={{ textTransform: 'capitalize' }}>
                {' '}
                {companyNamesSelectedList?.join(', ')}{' '}
                {companyNamesWithAliasesArray &&
                  companyNamesSelectedList &&
                  companyNamesWithAliasesArray?.length > companyNamesSelectedList?.length && (
                    <>({companyNamesWithAliasesArray.length})</>
                  )}
              </Typography>
            </Tooltip>
          </Typography>
          {isStatusError && (
            <Box mt={2}>
              <Typography color="error">Error: {stats?.errorMessage}</Typography>
            </Box>
          )}
          {isProcessing && (
            <Box>
              <>
                <CircularProgress />
                <Typography ml={3} component="span">
                  Status: {searchForBenchmarkStatus}.
                </Typography>
              </>
            </Box>
          )}
          {isFinished && (
            <>
              <Box mt={3}>
                Initial patents count: {stats?.initialPatentsCount} <br />
              </Box>
              <Box mt={3}>
                <Typography fontWeight="bold">Winning IPCR codes:</Typography>
                {stats?.winningIpcrCodes.map(row => (
                  <Typography fontSize={15}>{row}</Typography>
                ))}
              </Box>

              <Box mt={3}>
                {exists ? (
                  <Link href={`/dashboard/reporting/benchmark/${companyBecnhmarkResponse.completion_action_id}`}>
                    Open Benchmark Report
                  </Link>
                ) : (
                  <BenchmarkReportGenerator
                    id={companyBecnhmarkResponse.completion_action_id}
                    name={companyBecnhmarkResponse.company_names.split('||||')[0]}
                    ipcrCodes={selectedIpcrCodes}
                    origin="company_search"
                  />
                )}
              </Box>

              <Divider sx={{ mt: 5, mb: 5 }} />
              <Box mt={5}>
                <Typography fontWeight="bold">Company IPCR codes with prevalences:</Typography>
                {stats?.ipcrCodePrevalences.map(row => (
                  <Typography fontSize={15}>
                    {row.ipcrCode}: {row.prevalence.toFixed(2)}%
                  </Typography>
                ))}
              </Box>
            </>
          )}
          {isAdminRole && (
            <Box mt={3}>
              <CompanySearchLogs status={searchForBenchmarkStatus} stats={stats} />
            </Box>
          )}
        </>
      )}
    </Box>
  )
}
